import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../apiClient";

const parseNum = (raw) => {
  if (raw == null || raw === "") return NaN;
  const s = String(raw).replace(/[$,%\s,]/g, "");
  const v = Number(s);
  return Number.isFinite(v) ? v : NaN;
};

const fmtNumber = (v, maxFrac = 4) => {
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString(undefined, { maximumFractionDigits: maxFrac });
};

const fmtMoney0 = (v) => {
  if (!Number.isFinite(v)) return "";
  return `$${Math.round(v).toLocaleString()}`;
};

const fmtPercent2 = (v) => {
  if (!Number.isFinite(v)) return "";
  const s = v.toFixed(2);
  return `${s.replace(/\.00$/, "")}%`;
};

const fmtRate = (v) => {
  if (!Number.isFinite(v)) return "—";
  return `${v.toFixed(4).replace(/\.?0+$/, "")}%`;
};

function fileNameFromDisposition(headerValue, fallback) {
  const raw = String(headerValue || "");
  const utf8Match = raw.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const plainMatch = raw.match(/filename\s*=\s*"([^"]+)"/i) || raw.match(/filename\s*=\s*([^;]+)/i);
  return (plainMatch?.[1] || fallback || "savings_report.pdf").trim();
}

function downloadBlobUrl(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "savings_report.pdf";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function InternalNotesSection({ data, onChange, packet }) {
  const initial = (data && data.internal_notes) || {};
  const packetId = packet?.id ?? packet?.packet_id ?? null;

  const [notes, setNotes] = useState(initial.notes || "");
  const [calc, setCalc] = useState(initial.calc || { a: "", b: "" });
  const [cap, setCap] = useState(initial.cap || { noi: "", capRate: "", value: "" });
  const [taxSavings, setTaxSavings] = useState(
    initial.taxSavings || {
      originalValue: "",
      finalValue: "",
      rate1: "",
      rate2: "",
      rate3: "",
      rate4: "",
      rate5: "",
      rate6: "",
    }
  );

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportBlobUrl, setReportBlobUrl] = useState("");
  const [reportFilename, setReportFilename] = useState("savings_report.pdf");
  const [reportConfig, setReportConfig] = useState({
    resultType: "ARB",
    arbValue: "",
  });

  // Rehydrate only when server copy actually changes
  const serverHash = useMemo(
    () => JSON.stringify(data?.internal_notes || {}),
    [data?.internal_notes]
  );
  const lastServerHashRef = useRef(serverHash);

  useEffect(() => {
    if (serverHash !== lastServerHashRef.current) {
      const next = (data && data.internal_notes) || {};
      setNotes(next.notes || "");
      setCalc(next.calc || { a: "", b: "" });
      setCap(next.cap || { noi: "", capRate: "", value: "" });
      setTaxSavings(
        next.taxSavings || {
          originalValue: "",
          finalValue: "",
          rate1: "",
          rate2: "",
          rate3: "",
          rate4: "",
          rate5: "",
          rate6: "",
        }
      );
      lastServerHashRef.current = serverHash;
    }
  }, [serverHash, data?.internal_notes]);

  // Persist internal notes + tool state to draft.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onChange?.({
      internal_notes: {
        notes,
        calc,
        cap,
        taxSavings,
      },
    });
  }, [notes, calc, cap, taxSavings]);

  useEffect(() => {
    return () => {
      if (reportBlobUrl) URL.revokeObjectURL(reportBlobUrl);
    };
  }, [reportBlobUrl]);

  // Quick calculator derived values
  const aNum = parseNum(calc.a);
  const bNum = parseNum(calc.b);
  const sum = Number.isFinite(aNum) && Number.isFinite(bNum) ? aNum + bNum : NaN;
  const diff = Number.isFinite(aNum) && Number.isFinite(bNum) ? aNum - bNum : NaN;
  const prod = Number.isFinite(aNum) && Number.isFinite(bNum) ? aNum * bNum : NaN;
  const quot =
    Number.isFinite(aNum) && Number.isFinite(bNum) && bNum !== 0
      ? aNum / bNum
      : NaN;

  const solveValueFromCap = () => {
    const noiNum = parseNum(cap.noi);
    const capPct = parseNum(cap.capRate);
    if (!Number.isFinite(noiNum) || !Number.isFinite(capPct) || capPct === 0) return;
    const value = noiNum / (capPct / 100);
    setCap((prev) => ({ ...prev, value: fmtMoney0(value) }));
  };

  const solveCapFromValue = () => {
    const noiNum = parseNum(cap.noi);
    const valueNum = parseNum(cap.value);
    if (!Number.isFinite(noiNum) || !Number.isFinite(valueNum) || valueNum === 0) return;
    const capPct = (noiNum / valueNum) * 100;
    setCap((prev) => ({ ...prev, capRate: fmtPercent2(capPct) }));
  };

  const solveNoiFromValue = () => {
    const capPct = parseNum(cap.capRate);
    const valueNum = parseNum(cap.value);
    if (!Number.isFinite(capPct) || !Number.isFinite(valueNum) || capPct === 0) return;
    const noiNum = valueNum * (capPct / 100);
    setCap((prev) => ({ ...prev, noi: fmtMoney0(noiNum) }));
  };

  const tsOriginal = parseNum(taxSavings.originalValue);
  const tsFinal = parseNum(taxSavings.finalValue);
  const tsRates = [
    parseNum(taxSavings.rate1),
    parseNum(taxSavings.rate2),
    parseNum(taxSavings.rate3),
    parseNum(taxSavings.rate4),
    parseNum(taxSavings.rate5),
    parseNum(taxSavings.rate6),
  ];
  const tsTotalRate = tsRates.reduce(
    (sumRates, r) => sumRates + (Number.isFinite(r) ? r : 0),
    0
  );
  const tsReduction =
    Number.isFinite(tsOriginal) && Number.isFinite(tsFinal)
      ? tsOriginal - tsFinal
      : NaN;
  const tsSavings =
    Number.isFinite(tsReduction) && Number.isFinite(tsTotalRate)
      ? tsReduction * (tsTotalRate / 100)
      : NaN;

  const arbNum = parseNum(reportConfig.arbValue);
  const multiStep = reportConfig.resultType === "RBA" || reportConfig.resultType === "LITIGATION";

  const canGenerateBase =
    !!packetId &&
    Number.isFinite(tsOriginal) &&
    Number.isFinite(tsFinal) &&
    Number.isFinite(tsTotalRate) &&
    tsTotalRate > 0;

  const canPreview =
    canGenerateBase &&
    (!multiStep || Number.isFinite(arbNum));

  const openSavingsReportModal = () => {
    setReportError("");
    setReportModalOpen(true);
  };

  const closeSavingsReportModal = () => {
    if (reportBusy) return;
    setReportError("");
    setReportModalOpen(false);
  };

  const closeSavingsPreview = () => {
    setReportPreviewOpen(false);
  };

  const requestSavingsReport = async ({ download = false } = {}) => {
    if (!packetId) {
      setReportError("Please save this packet before generating a savings report.");
      return;
    }

    if (!Number.isFinite(tsOriginal) || !Number.isFinite(tsFinal)) {
      setReportError("Enter both Original Value and Final Value first.");
      return;
    }

    if (!Number.isFinite(tsTotalRate) || tsTotalRate <= 0) {
      setReportError("Enter at least one tax rate before generating the report.");
      return;
    }

    if (multiStep && !Number.isFinite(arbNum)) {
      setReportError("Enter an ARB value for RBA or Litigation reports.");
      return;
    }

    setReportBusy(true);
    setReportError("");

    try {
      const payload = {
        result_type: reportConfig.resultType,
        original_value: taxSavings.originalValue,
        final_value: taxSavings.finalValue,
        arb_value: multiStep ? reportConfig.arbValue : null,
        rate_components: [
          taxSavings.rate1,
          taxSavings.rate2,
          taxSavings.rate3,
          taxSavings.rate4,
          taxSavings.rate5,
          taxSavings.rate6,
        ],
      };

      const res = await api.post(
        `/bapb/packets/${packetId}/export/savings-report`,
        payload,
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);
      const fallbackName = `packet_${packetId}_${String(reportConfig.resultType || "arb").toLowerCase()}_savings_report.pdf`;
      const filename = fileNameFromDisposition(
        res?.headers?.["content-disposition"],
        fallbackName
      );

      if (download) {
        downloadBlobUrl(blobUrl, filename);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        return;
      }

      if (reportBlobUrl) URL.revokeObjectURL(reportBlobUrl);
      setReportFilename(filename);
      setReportBlobUrl(blobUrl);
      setReportModalOpen(false);
      setReportPreviewOpen(true);
    } catch (err) {
      setReportError(err?.response?.data?.detail || "Failed to generate savings report.");
    } finally {
      setReportBusy(false);
    }
  };

  const downloadPreviewPdf = () => {
    if (!reportBlobUrl) return;
    downloadBlobUrl(reportBlobUrl, reportFilename);
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm">
        <div className="font-medium text-slate-800 mb-1">Internal Notes &amp; Tools</div>
        <div className="text-xs text-slate-600">
          Internal notes stay internal. The new Savings Report button below generates a
          separate client-facing PDF from your tax savings inputs.
        </div>
      </div>

      {/* Notes textarea */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
        <label className="block text-xs text-gray-500 mb-1">
          Notes (internal only)
        </label>
        <textarea
          rows={6}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          placeholder="Scratchpad for arguments, hearing notes, settlement thoughts, etc."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Tools */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-4">
        <div className="text-sm font-medium text-slate-800">
          Quick tools
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Quick Calculator */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-700 mb-1">
              Quick calculator
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">
                  A
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  value={calc.a}
                  onChange={(e) =>
                    setCalc((prev) => ({ ...prev, a: e.target.value }))
                  }
                  placeholder="e.g., 1250000"
                  inputMode="decimal"
                />
              </div>
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">
                  B
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  value={calc.b}
                  onChange={(e) =>
                    setCalc((prev) => ({ ...prev, b: e.target.value }))
                  }
                  placeholder="e.g., 725000"
                  inputMode="decimal"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-1 text-[11px] mt-1">
              <ToolRow label="A + B" value={fmtNumber(sum)} />
              <ToolRow label="A − B" value={fmtNumber(diff)} />
              <ToolRow label="A × B" value={fmtNumber(prod)} />
              <ToolRow label="A ÷ B" value={fmtNumber(quot)} />

              <ToolRow
                label="A is % of B"
                value={
                  Number.isFinite(aNum) &&
                  Number.isFinite(bNum) &&
                  bNum !== 0
                    ? fmtNumber((aNum / bNum) * 100, 2) + "%"
                    : "—"
                }
              />

              <ToolRow
                label="B is % of A"
                value={
                  Number.isFinite(aNum) &&
                  Number.isFinite(bNum) &&
                  aNum !== 0
                    ? fmtNumber((bNum / aNum) * 100, 2) + "%"
                    : "—"
                }
              />

              <ToolRow
                label="A is % higher than B"
                value={
                  Number.isFinite(aNum) &&
                  Number.isFinite(bNum) &&
                  bNum !== 0
                    ? fmtNumber(((aNum - bNum) / bNum) * 100, 2) + "%"
                    : "—"
                }
              />

              <ToolRow
                label="A is % lower than B"
                value={
                  Number.isFinite(aNum) &&
                  Number.isFinite(bNum) &&
                  bNum !== 0
                    ? fmtNumber(((bNum - aNum) / bNum) * 100, 2) + "%"
                    : "—"
                }
              />

              <ToolRow
                label="B is % higher than A"
                value={
                  Number.isFinite(aNum) &&
                  Number.isFinite(bNum) &&
                  aNum !== 0
                    ? fmtNumber(((bNum - aNum) / aNum) * 100, 2) + "%"
                    : "—"
                }
              />

              <ToolRow
                label="B is % lower than A"
                value={
                  Number.isFinite(aNum) &&
                  Number.isFinite(bNum) &&
                  aNum !== 0
                    ? fmtNumber(((aNum - bNum) / aNum) * 100, 2) + "%"
                    : "—"
                }
              />
            </div>
          </div>

          {/* Cap Rate helper */}
          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
            <div className="text-xs font-semibold text-slate-700 mb-1">
              Cap rate helper
            </div>
            <div className="grid gap-2">
              <div>
                <label className="block text-[11px] text-slate-500 mb-1">
                  NOI (annual)
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  value={cap.noi}
                  onChange={(e) =>
                    setCap((prev) => ({ ...prev, noi: e.target.value }))
                  }
                  placeholder="$"
                  inputMode="decimal"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">
                    Cap rate
                  </label>
                  <input
                    className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    value={cap.capRate}
                    onChange={(e) =>
                      setCap((prev) => ({ ...prev, capRate: e.target.value }))
                    }
                    placeholder="%"
                    inputMode="decimal"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">
                    Value
                  </label>
                  <input
                    className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                    value={cap.value}
                    onChange={(e) =>
                      setCap((prev) => ({ ...prev, value: e.target.value }))
                    }
                    placeholder="$"
                    inputMode="decimal"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-1">
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:border-slate-400"
                onClick={solveValueFromCap}
              >
                Solve Value from NOI & Cap
              </button>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:border-slate-400"
                onClick={solveCapFromValue}
              >
                Solve Cap % from NOI & Value
              </button>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs hover:border-slate-400"
                onClick={solveNoiFromValue}
              >
                Solve NOI from Value & Cap
              </button>
            </div>

            <div className="text-[11px] text-slate-500">
              Quick sanity checks only – the actual valuation still lives in your
              Income &amp; E&amp;U sections.
            </div>
          </div>
        </div>

        {/* Tax Savings Tool */}
        <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">
                Tax savings tool
              </div>
              <div className="text-[11px] text-slate-500">
                Use these values to generate a polished standalone savings report PDF.
              </div>
            </div>

            <button
              type="button"
              onClick={openSavingsReportModal}
              disabled={!canGenerateBase}
              className={[
                "rounded-lg px-3 py-2 text-sm font-medium border",
                canGenerateBase
                  ? "border-sky-200 bg-white text-sky-700 hover:border-sky-300"
                  : "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed",
              ].join(" ")}
              title={
                canGenerateBase
                  ? "Generate savings report"
                  : "Enter original value, final value, and at least one tax rate first"
              }
            >
              Generate Savings Report
            </button>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">
                Original value
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={taxSavings.originalValue}
                onChange={(e) =>
                  setTaxSavings((prev) => ({
                    ...prev,
                    originalValue: e.target.value,
                  }))
                }
                placeholder="e.g., 25000000"
                inputMode="decimal"
              />
            </div>
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">
                Final value
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                value={taxSavings.finalValue}
                onChange={(e) =>
                  setTaxSavings((prev) => ({
                    ...prev,
                    finalValue: e.target.value,
                  }))
                }
                placeholder="e.g., 21774010"
                inputMode="decimal"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] text-slate-500 mb-2">
              Jurisdiction rates (enter whole-number percentages, so 1.15 = 1.15%)
            </label>
            <div className="grid gap-2 md:grid-cols-3">
              <RateInput
                label="Rate 1"
                value={taxSavings.rate1}
                onChange={(v) => setTaxSavings((prev) => ({ ...prev, rate1: v }))}
              />
              <RateInput
                label="Rate 2"
                value={taxSavings.rate2}
                onChange={(v) => setTaxSavings((prev) => ({ ...prev, rate2: v }))}
              />
              <RateInput
                label="Rate 3"
                value={taxSavings.rate3}
                onChange={(v) => setTaxSavings((prev) => ({ ...prev, rate3: v }))}
              />
              <RateInput
                label="Rate 4"
                value={taxSavings.rate4}
                onChange={(v) => setTaxSavings((prev) => ({ ...prev, rate4: v }))}
              />
              <RateInput
                label="Rate 5"
                value={taxSavings.rate5}
                onChange={(v) => setTaxSavings((prev) => ({ ...prev, rate5: v }))}
              />
              <RateInput
                label="Rate 6"
                value={taxSavings.rate6}
                onChange={(v) => setTaxSavings((prev) => ({ ...prev, rate6: v }))}
              />
            </div>
          </div>

          <div className="grid gap-2 md:grid-cols-3">
            <ToolRowCard
              label="Combined tax rate"
              value={fmtRate(tsTotalRate)}
            />
            <ToolRowCard
              label="Value reduction"
              value={Number.isFinite(tsReduction) ? fmtMoney0(tsReduction) : "—"}
            />
            <ToolRowCard
              label="Estimated tax savings"
              value={Number.isFinite(tsSavings) ? fmtMoney0(tsSavings) : "—"}
              accent
            />
          </div>

          <div className="text-[11px] text-slate-500">
            Formula: (Original Value − Final Value) × Combined Tax Rate
          </div>

          {!packetId && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
              Save this packet first. The savings report generator needs a saved packet so it
              can pull the cover photo and property facts.
            </div>
          )}
        </div>
      </div>

      {reportModalOpen && (
        <ModalShell
          title="Generate Savings Report"
          subtitle="Choose the result type and preview the PDF before downloading."
          onClose={closeSavingsReportModal}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs text-slate-500 mb-2">Result type</label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <ResultTypeButton
                  active={reportConfig.resultType === "ARB"}
                  onClick={() => setReportConfig((prev) => ({ ...prev, resultType: "ARB" }))}
                  label="ARB"
                />
                <ResultTypeButton
                  active={reportConfig.resultType === "RBA"}
                  onClick={() => setReportConfig((prev) => ({ ...prev, resultType: "RBA" }))}
                  label="RBA"
                />
                <ResultTypeButton
                  active={reportConfig.resultType === "LITIGATION"}
                  onClick={() => setReportConfig((prev) => ({ ...prev, resultType: "LITIGATION" }))}
                  label="Litigation"
                />
              </div>
            </div>

            <div className={`grid gap-3 ${multiStep ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Original value</label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                  value={taxSavings.originalValue}
                  readOnly
                />
              </div>

              {multiStep && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">ARB value</label>
                  <input
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={reportConfig.arbValue}
                    onChange={(e) =>
                      setReportConfig((prev) => ({ ...prev, arbValue: e.target.value }))
                    }
                    placeholder="e.g., 23000000"
                    inputMode="decimal"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs text-slate-500 mb-1">
                  {multiStep ? "Final RBA / Litigation value" : "Final value"}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-slate-50"
                  value={taxSavings.finalValue}
                  readOnly
                />
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              {multiStep ? (
                <>
                  The report will show savings from <strong>Original → ARB</strong>,{" "}
                  <strong>ARB → {reportConfig.resultType === "RBA" ? "RBA" : "Litigation"}</strong>,
                  and the <strong>total Original → Final</strong> reduction.
                </>
              ) : (
                <>
                  The report will show a direct savings calculation from{" "}
                  <strong>Original → ARB</strong>.
                </>
              )}
            </div>

            {reportError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {reportError}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:border-slate-400"
                onClick={closeSavingsReportModal}
                disabled={reportBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 hover:border-sky-300 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => requestSavingsReport({ download: false })}
                disabled={!canPreview || reportBusy}
              >
                {reportBusy ? "Generating…" : "Preview PDF"}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {reportPreviewOpen && (
        <div className="fixed inset-0 z-[80] bg-slate-950/60 p-4">
          <div className="mx-auto flex h-full w-full max-w-6xl flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <div className="text-base font-semibold text-slate-800">Savings Report Preview</div>
                <div className="text-xs text-slate-500">{reportFilename}</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 hover:border-sky-300"
                  onClick={downloadPreviewPdf}
                >
                  Download PDF
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:border-slate-400"
                  onClick={closeSavingsPreview}
                >
                  Close
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 bg-slate-100 p-3">
              {reportBlobUrl ? (
                <iframe
                  title="Savings Report Preview"
                  src={reportBlobUrl}
                  className="h-full w-full rounded-xl border border-slate-200 bg-white"
                />
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  No preview available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToolRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-1">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono text-slate-800">{value}</span>
    </div>
  );
}

function RateInput({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] text-slate-500 mb-1">{label}</label>
      <input
        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g., 1.15"
        inputMode="decimal"
      />
    </div>
  );
}

function ToolRowCard({ label, value, accent = false }) {
  return (
    <div
      className={[
        "rounded-lg border px-3 py-2",
        accent
          ? "border-emerald-200 bg-emerald-50"
          : "border-slate-200 bg-white",
      ].join(" ")}
    >
      <div className="text-[11px] text-slate-500 mb-1">{label}</div>
      <div
        className={[
          "font-mono text-sm",
          accent ? "text-emerald-800 font-semibold" : "text-slate-800",
        ].join(" ")}
      >
        {value}
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/40 p-4" onMouseDown={onClose}>
      <div
        className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="text-base font-semibold text-slate-800">{title}</div>
          {subtitle ? <div className="text-xs text-slate-500 mt-1">{subtitle}</div> : null}
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function ResultTypeButton({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-lg border px-3 py-2 text-sm font-medium transition",
        active
          ? "border-sky-300 bg-sky-50 text-sky-700"
          : "border-slate-300 bg-white text-slate-700 hover:border-slate-400",
      ].join(" ")}
    >
      {label}
    </button>
  );
}